import React, { useEffect, useRef, useState } from "react";
import { FiCheck } from "react-icons/fi";
import { usePrefersReducedMotion } from "./motion";

/*
 * A laser printer on a counter. `PrinterUnit` is the printer itself (used by the hero story and
 * the standalone loop below); `Printer` runs its own loop: a sheet feeds out of the slot, pauses,
 * drops onto the pile beside the printer; after three sheets the order is marked ready and the
 * pile clears. It runs only while on screen and freezes to the finished order when the visitor
 * prefers reduced motion or `still` is set (the login page).
 */

export const ORDER_NO = "2312";
const SHEETS = 3;
const T = { start: 600, feed: 2600, hold: 450, land: 700, settle: 90, ready: 3400, clear: 600 };
const range = (n) => Array.from({ length: n }, (_, i) => i);
const ALL = range(SHEETS);

/** The printer: top cover with the slot, body with display and LED, paper tray. `children` sit inside the unit. */
export function PrinterUnit({ label = "Ready", children }) {
  return (
    <div className="press__unit">
      <div className="press__top">
        <span className="press__glow" />
      </div>
      <div className="press__body">
        <div className="press__display">
          <span className="press__led" />
          <em>{label}</em>
        </div>
        <span className="press__vent" />
      </div>
      <div className="press__tray" />
      {children}
    </div>
  );
}

export function ShopSign() {
  return (
    <div className="press__sign">
      <img src="/img/print_anywhere_logo2.webp" alt="" />
      <span>
        <strong>Partner shop</strong>
        <small>Open now</small>
      </span>
    </div>
  );
}

export function ReadyStamp({ pages = 3 }) {
  return (
    <div className="press__stamp">
      <span className="press__stamp-tick">
        <FiCheck />
      </span>
      <span>
        Ready to collect
        <small>
          Order {ORDER_NO}, {pages} pages
        </small>
      </span>
    </div>
  );
}

export default function Printer({ still = false, className = "" }) {
  const reduced = usePrefersReducedMotion();
  const frozen = still || reduced;
  const [state, setState] = useState(() => (frozen ? { phase: "ready", sheet: 0, stack: ALL } : { phase: "idle", sheet: 0, stack: [] }));
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(() => (typeof document === "undefined" ? true : !document.hidden));
  const rootRef = useRef(null);
  const sheetRef = useRef(null);
  const stackRef = useRef(null);

  useEffect(() => {
    if (frozen) setState({ phase: "ready", sheet: 0, stack: ALL });
  }, [frozen]);

  useEffect(() => {
    if (frozen) return undefined;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    const onVisibility = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [frozen]);

  const active = !frozen && inView && visible;

  useEffect(() => {
    if (!active) {
      if (!frozen) setState({ phase: "idle", sheet: 0, stack: [] });
      return undefined;
    }
    let cancelled = false;
    let timer = 0;
    const wait = (ms) =>
      new Promise((resolve) => {
        timer = setTimeout(resolve, ms);
      });
    const aim = (i) => {
      const sheet = sheetRef.current;
      const stack = stackRef.current;
      if (!sheet || !stack) return;
      const a = sheet.getBoundingClientRect();
      const b = stack.getBoundingClientRect();
      sheet.style.setProperty("--land-x", `${b.left - a.left + i * 4}px`);
      sheet.style.setProperty("--land-y", `${b.bottom - a.bottom - i * 3}px`);
      sheet.style.setProperty("--land-r", `${(i - 1) * 3}deg`);
    };
    (async () => {
      await wait(T.start);
      while (!cancelled) {
        for (let i = 0; i < SHEETS; i += 1) {
          setState({ phase: "feed", sheet: i, stack: range(i) });
          await wait(T.feed);
          if (cancelled) return;
          setState({ phase: "hold", sheet: i, stack: range(i) });
          await wait(T.hold);
          if (cancelled) return;
          aim(i);
          setState({ phase: "land", sheet: i, stack: range(i) });
          await wait(T.land);
          if (cancelled) return;
          setState({ phase: "stacked", sheet: i, stack: range(i + 1) });
          await wait(T.settle);
          if (cancelled) return;
        }
        setState({ phase: "ready", sheet: 0, stack: ALL });
        await wait(T.ready);
        if (cancelled) return;
        setState({ phase: "clear", sheet: 0, stack: ALL });
        await wait(T.clear);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, frozen]);

  const { phase, sheet, stack } = state;
  const busy = phase === "feed" || phase === "hold" || phase === "land" || phase === "stacked";
  const label = busy ? `Printing ${Math.min(sheet + 1, SHEETS)} of ${SHEETS}` : phase === "ready" ? `Order ${ORDER_NO} done` : "Ready";

  return (
    <div className={`press is-${phase} ${className}`} ref={rootRef} aria-hidden="true">
      <div className="press__bg" />
      <div className="press__counter" />
      <ShopSign />
      <PrinterUnit label={label}>
        <div className="press__slot">
          <div className="press__sheet" ref={sheetRef}>
            <Sheet kind={sheet} />
          </div>
        </div>
      </PrinterUnit>
      <div className="press__stack" ref={stackRef}>
        {stack.map((i) => (
          <div className="press__pile" style={{ "--i": i }} key={i}>
            <Sheet kind={i} />
          </div>
        ))}
      </div>
      {phase === "ready" ? <ReadyStamp /> : null}
    </div>
  );
}

/* ---------------------------------------------------------------- the pages */

const PAGES = [
  { title: "Assignment 3", sub: "Data Structures, Semester 4", page: "1 / 3" },
  { title: "Lab report", sub: "Results and observations", page: "2 / 3" },
  { title: "Hall ticket", sub: "Winter examination", page: "3 / 3" },
];

/** A printed page. Kind 0 and 2 are black and white, kind 1 has a colour chart. */
export function Sheet({ kind = 0 }) {
  const meta = PAGES[kind % PAGES.length];
  return (
    <div className={`sheet sheet--${kind % PAGES.length}`}>
      <div className="sheet__head">
        <b>{meta.title}</b>
        <span>{meta.sub}</span>
      </div>
      <span className="sheet__rule" />
      {kind % 3 === 0 ? (
        <>
          <div className="sheet__meta">
            <span>Name</span>
            <i style={{ width: "34%" }} />
            <span>Roll no</span>
            <i style={{ width: "16%" }} />
          </div>
          <div className="sheet__para">
            <i style={{ width: "96%" }} />
            <i style={{ width: "88%" }} />
            <i style={{ width: "93%" }} />
            <i style={{ width: "62%" }} />
          </div>
          <div className="sheet__para">
            <i style={{ width: "40%" }} className="is-dark" />
            <i style={{ width: "92%" }} />
            <i style={{ width: "85%" }} />
            <i style={{ width: "90%" }} />
            <i style={{ width: "48%" }} />
          </div>
        </>
      ) : null}
      {kind % 3 === 1 ? (
        <>
          <div className="sheet__chart">
            <i style={{ height: "42%", background: "#f25c05" }} />
            <i style={{ height: "68%", background: "#ff3d71" }} />
            <i style={{ height: "55%", background: "#2457d6" }} />
            <i style={{ height: "88%", background: "#f25c05" }} />
            <i style={{ height: "30%", background: "#ff3d71" }} />
            <i style={{ height: "74%", background: "#2457d6" }} />
          </div>
          <div className="sheet__legend">
            <span>
              <i style={{ background: "#f25c05" }} /> Trial 1
            </span>
            <span>
              <i style={{ background: "#ff3d71" }} /> Trial 2
            </span>
            <span>
              <i style={{ background: "#2457d6" }} /> Control
            </span>
          </div>
          <div className="sheet__para">
            <i style={{ width: "94%" }} />
            <i style={{ width: "86%" }} />
            <i style={{ width: "58%" }} />
          </div>
        </>
      ) : null}
      {kind % 3 === 2 ? (
        <>
          <div className="sheet__ticket">
            <span className="sheet__photo" />
            <div className="sheet__para">
              <i style={{ width: "70%" }} className="is-dark" />
              <i style={{ width: "55%" }} />
              <i style={{ width: "80%" }} />
              <i style={{ width: "45%" }} />
            </div>
          </div>
          <span className="sheet__barcode" />
          <div className="sheet__para">
            <i style={{ width: "90%" }} />
            <i style={{ width: "66%" }} />
          </div>
        </>
      ) : null}
      <div className="sheet__foot">
        <span>Order {ORDER_NO}</span>
        <span>{meta.page}</span>
      </div>
    </div>
  );
}
